import useSWR, { mutate as globalMutate } from 'swr';
import useSWRInfinite from 'swr/infinite';
import type { FormatId } from '../lib/formats';
import { parseErrorResponse } from './problem';

// Item 97 Phase C: list endpoints opt into a {items, next_cursor,
// has_more} envelope when called with `?paginated=1`. The legacy array
// shape is preserved for callers that don't pass the flag, so the
// existing useProjects()/useRepos() hooks keep their array contract.
export interface PageEnvelope<T> {
  items: T[];
  next_cursor?: string;
  has_more: boolean;
}

// usePaginated wraps useSWRInfinite for endpoints that speak the
// envelope shape. `keyBase` is the URL up to but not including the
// pagination params; the helper appends `paginated=1`, `limit`, and
// (on pages >0) `cursor`. Passing `null` for keyBase pauses fetching
// the same way useSWR does — handy when the caller is still resolving
// auth or a route param.
export function usePaginated<T>(keyBase: string | null, limit = 50) {
  const sep = keyBase?.includes('?') ? '&' : '?';
  const swr = useSWRInfinite<PageEnvelope<T>>(
    (_idx, prev) => {
      if (!keyBase) return null;
      if (prev && !prev.has_more) return null;
      const cursor = prev?.next_cursor ?? '';
      const cursorPart = cursor ? `&cursor=${encodeURIComponent(cursor)}` : '';
      const tail = `paginated=1&limit=${limit}${cursorPart}`;
      return `${keyBase}${sep}${tail}`;
    },
    fetcher,
  );
  const items = swr.data ? swr.data.flatMap((p) => p.items) : [];
  const last = swr.data?.[swr.data.length - 1];
  return {
    items,
    error: swr.error,
    isLoading: swr.isLoading,
    isValidating: swr.isValidating,
    hasMore: last?.has_more ?? false,
    loadMore: () => swr.setSize(swr.size + 1),
    mutate: swr.mutate,
    size: swr.size,
  };
}

export type User = {
  email: string;
  displayName?: string;
  objectID?: string;
  tenantID?: string;
  groups?: string[];
  roles?: string[];
  // Item 139: false for a principal with no org_admin role and no
  // role_bindings row (project-scoped or org-wide) — a config-mapped
  // reader/developer role alone doesn't grant one. Lets the frontend
  // show an explanatory empty state instead of a bare empty list.
  hasAnyBinding?: boolean;
  // Item 210: set while an org_admin is impersonating this user —
  // carries the ADMIN's email (the real actor), not the target's.
  // The rest of the User fields (email, roles, ...) already describe
  // the impersonation TARGET, matching what every other API call
  // sees for the duration of the session.
  impersonatedBy?: string;
};

export type Project = {
  id: string;
  slug: string;
  name: string;
  description?: string;
};

export type Repo = {
  id: string;
  projectId: string;
  slug: string;
  name: string;
  kind: 'local' | 'remote' | 'virtual';
  format: FormatId;
  upstreamUrl?: string;
  // Ordered list of member-repo UUIDs that back a virtual repo. The
  // backend resolves them in order on pull. Empty / undefined for
  // local + remote.
  members?: string[];
  // Set by the backend when an existing repo has a kind its format cannot
  // serve (item 414); the repo is kept, only flagged.
  capabilityWarning?: string;
  // Connection probe result for remote repos on create/PATCH (item 421).
  // A warning never blocks creation (air-gap preparation stays possible).
  upstreamCheck?: { status: 'ok' | 'warning'; reason?: string; probe?: string };
};

// Patch shape for PATCH /api/v1/repos/{id}. Only fields actually sent
// move on the backend; everything else stays at its current value.
// The backend rejects changes to id and projectId; everything else
// (slug / name / kind / format / upstreamUrl / members) is editable.
export type RepoPatch = Partial<Pick<Repo,
  'slug' | 'name' | 'kind' | 'format' | 'upstreamUrl' | 'members'
>>;

async function fetcher<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    // The Accept: application/json header makes the auth middleware
    // return 401 instead of redirecting to /login when there's no
    // session — important for SWR to fail cleanly.
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json() as Promise<T>;
}

export function useWhoami() {
  const { data, error, isLoading } = useSWR<User>('/auth/whoami', fetcher, {
    shouldRetryOnError: false,
  });
  return { user: data, loading: isLoading, error };
}

export function useProjects() {
  return useSWR<Project[]>('/api/v1/projects', fetcher);
}

// Item 97 Phase C: cursor-paginated variant of useProjects. The
// daily-driver Projects.tsx list switches to this so a customer with
// hundreds of projects gets a "Show more" button instead of a silent
// truncation at the legacy default of 200.
export function useProjectsPaginated(limit = 50) {
  return usePaginated<Project>('/api/v1/projects', limit);
}

// Repo kinds each format really serves (item 414); from GET /api/v1/formats.
export interface FormatCapability { id: string; remote: boolean; virtual: boolean }
export function useFormatCapabilities() {
  return useSWR<{ formats: FormatCapability[] }>('/api/v1/formats', fetcher);
}

export function useRepos() {
  return useSWR<Repo[]>('/api/v1/repos', fetcher);
}

// startImpersonation/endImpersonation drive the org_admin-only
// /api/v1/impersonation/{start,end} pair (item 210). Both endpoints
// respond 204 with no body — the only observable effect is the
// `or_impersonation` cookie, so we re-fetch /auth/whoami afterwards
// to pick up the swapped identity everywhere useWhoami() is used.
export async function startImpersonation(targetEmail: string) {
  const res = await fetch('/api/v1/impersonation/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ target_email: targetEmail }),
  });
  if (!res.ok) throw await parseErrorResponse(res);
  await globalMutate('/auth/whoami');
}

export async function endImpersonation() {
  const res = await fetch('/api/v1/impersonation/end', {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw await parseErrorResponse(res);
  await globalMutate('/auth/whoami');
}

export async function createProject(body: Omit<Project, 'id'>) {
  const res = await fetch('/api/v1/projects', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json() as Promise<Project>;
}

export async function createRepo(body: Omit<Repo, 'id'>) {
  const res = await fetch('/api/v1/repos', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json() as Promise<Repo>;
}

// updateRepo PATCHes an existing repo. Only the fields present in
// `patch` move; the backend leaves the rest untouched.
export async function updateRepo(id: string, patch: RepoPatch) {
  const res = await fetch(`/api/v1/repos/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json() as Promise<Repo>;
}

export interface RepoArtifact {
  id: string;
  path: string;
  version: string;
  size_bytes: number;
  sha256: string;
  content_type: string;
  uploaded_at: string;
  metadata: Record<string, unknown>;
}

export function useRepoDetail(id: string | undefined) {
  return useSWR<Repo>(id ? `/api/v1/repos/${id}` : null, fetcher);
}

export function useRepoArtifacts(id: string | undefined, limit = 200) {
  return useSWR<RepoArtifact[]>(
    id ? `/api/v1/repos/${id}/artifacts?limit=${limit}` : null,
    fetcher,
  );
}

// v186 — project-owner editable retention policies. The endpoint is
// gated by maintainer-or-higher; non-members get a 403 (handled by
// SWR as an error and rendered as "no permission" in the UI).
export interface RetentionRule {
  max_age_days?: number;
  keep_per_coord?: number;
}

export interface RetentionPolicy {
  id?: string;
  name: string;
  rule: RetentionRule;
  enabled: boolean;
  dry_run: boolean;
  last_run_at?: string | null;
  created_at?: string | null;
}

// Item 406 Phase A — per-virtual-repo "deliver the safe version" mode.
// Admin-only endpoint: a non-admin gets 403 → SWR error, and the card hides.
export type SafeVersionMode = 'off' | 'patch' | 'minor';

export function useRepoSafeVersion(id: string | undefined) {
  return useSWR<{ repo_id: string; mode: SafeVersionMode }>(
    id ? `/api/admin/repos/${id}/safe-version` : null,
    fetcher,
    { shouldRetryOnError: false },
  );
}

export async function setRepoSafeVersion(repoID: string, mode: SafeVersionMode) {
  const res = await fetch(`/api/admin/repos/${repoID}/safe-version`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ mode }),
  });
  if (!res.ok) throw await parseErrorResponse(res);
}

// useRepoRetention returns SWR-managed retention policies for a repo.
// Returns 403 → SWR error which the caller can render as "you don't
// have permission to edit retention here".
export function useRepoRetention(id: string | undefined) {
  return useSWR<RetentionPolicy[]>(
    id ? `/api/v1/repos/${id}/retention` : null,
    fetcher,
  );
}

export async function createRepoRetention(
  repoID: string,
  body: RetentionPolicy,
) {
  const res = await fetch(`/api/v1/repos/${repoID}/retention`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json() as Promise<RetentionPolicy>;
}

export async function updateRepoRetention(
  repoID: string,
  name: string,
  body: RetentionPolicy,
) {
  const res = await fetch(
    `/api/v1/repos/${repoID}/retention/${encodeURIComponent(name)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) throw await parseErrorResponse(res);
}

export async function deleteRepoRetention(repoID: string, name: string) {
  const res = await fetch(
    `/api/v1/repos/${repoID}/retention/${encodeURIComponent(name)}`,
    {
      method: 'DELETE',
      credentials: 'include',
    },
  );
  if (!res.ok) throw await parseErrorResponse(res);
}

// Soft-delete a repository (move to trash). project_admin or higher; the
// backend hides it immediately and the /trash view can restore it for 30 days.
export async function deleteRepo(repoID: string) {
  const res = await fetch(`/api/v1/repos/${repoID}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw await parseErrorResponse(res);
}

// Soft-delete a single artifact version (move to trash). project_admin+.
export async function deleteArtifact(artifactID: string) {
  const res = await fetch(`/api/v1/artifacts/${artifactID}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw await parseErrorResponse(res);
}

// Soft-delete a whole project + its repos (move to trash). project_admin+.
export async function deleteProject(projectID: string) {
  const res = await fetch(`/api/v1/projects/${projectID}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw await parseErrorResponse(res);
}

// v190 — per-repo stats dashboard (roadmap item 14). Returns the
// 30-day pull sparkline, top consumers (only populated for
// maintainer+ callers — see backend), top artifacts by pull count,
// and storage totals. The endpoint is project-read gated; SWR will
// surface a 403 as `error` for repos the caller can't see.
export interface RepoStatsDailyPull {
  day: string; // YYYY-MM-DD
  count: number;
}

export interface RepoStatsTopConsumer {
  principal_email: string;
  principal_kind: string;
  count: number;
}

export interface RepoStatsTopArtifact {
  artifact_id: string;
  path: string;
  version?: string;
  sha256: string;
  size_bytes: number;
  pull_count: number;
}

export interface RepoStats {
  window_days: number;
  storage_total_bytes: number;
  storage_growth_30d: number;
  artifact_count: number;
  pulls_total_30d: number;
  pulls_30d_daily: RepoStatsDailyPull[];
  top_consumers: RepoStatsTopConsumer[];
  top_artifacts_by_pulls: RepoStatsTopArtifact[];
  cache_hit_rate_30d: number | null;
}

export function useRepoStats(id: string | undefined) {
  return useSWR<RepoStats>(
    id ? `/api/v1/repos/${id}/stats` : null,
    fetcher,
  );
}

// Roadmap item 323 Phase B — data layer for the virtual-repo provenance
// graph. Backend-computed win/shadow counts per member; see
// GET /api/v1/repos/{id}/resolution-stats (repo_resolution_stats.go).
export interface RepoResolutionStatsShadowedBy {
  repo_id: string;
  count: number;
}

export interface RepoResolutionStatsMember {
  repo_id: string;
  priority: number;
  won_count: number;
  shadowed_count: number;
  shadowed_by: RepoResolutionStatsShadowedBy[];
}

export interface RepoResolutionStats {
  members: RepoResolutionStatsMember[];
}

// Only virtual repos have a resolution-stats endpoint (400 otherwise), so
// callers must pass the repo's kind and get a paused SWR key for the rest.
export function useRepoResolutionStats(id: string | undefined, kind: Repo['kind'] | undefined) {
  return useSWR<RepoResolutionStats>(
    id && kind === 'virtual' ? `/api/v1/repos/${id}/resolution-stats` : null,
    fetcher,
  );
}

// Roadmap item 323 Phase C — the coordinates behind one provenance-graph
// edge. `winner_repo_id` is only present on `status=shadowed` rows (see
// repo_resolution_coordinates.go); omitted (not empty) for `won` rows.
export interface RepoResolutionCoordinate {
  path: string;
  version?: string;
  uploaded_at: string;
  winner_repo_id?: string;
}

// useRepoResolutionCoordinates — paginated drill-down for exactly one
// (member, status) edge, paused until the caller supplies both (the graph
// only knows which edge was clicked once a member node/edge is selected).
export function useRepoResolutionCoordinates(
  repoId: string | undefined,
  memberId: string | undefined,
  status: 'won' | 'shadowed' | undefined,
  limit = 25,
) {
  const keyBase =
    repoId && memberId && status
      ? `/api/v1/repos/${repoId}/resolution-stats/coordinates?member=${encodeURIComponent(memberId)}&status=${status}`
      : null;
  return usePaginated<RepoResolutionCoordinate>(keyBase, limit);
}

// Item 405 Phase C: per-project MCP approval policy (admin-only endpoint;
// non-admins get a 403 which the MCP page renders as "status unavailable").
export interface McpApproval {
  name: string;
  version: string;
  decision: 'approved' | 'denied' | string;
  allowed_tools: string[];
  denied_tools: string[];
}
export interface McpPolicy {
  project_id: string;
  enforce: boolean;
  servers: McpApproval[];
}
export function useMcpPolicy(projectId: string | undefined) {
  return useSWR<McpPolicy>(
    projectId ? `/api/admin/mcp-policy/${projectId}` : null,
    fetcher,
    { shouldRetryOnError: false },
  );
}
